/*
  Admin › Settings (26) — wave 2 idea 2: the theme as three small cards with a drawn thumbnail
  (sidebar · canvas · two blocks · a lime pill), a radio and a "Current" badge. Picking one previews it
  in this browser only (AppSettingsContext.previewTheme) until the admin saves or discards.
  The thumbnails draw the themes themselves, so their colours are constant (they do not follow the theme).
*/
import { useRef } from 'react';
import { Eye, Palette } from 'lucide-react';
import { cn } from '@/lib/utils';
import { THEME_OPTIONS } from '@/contexts/AppSettingsContext';

export const THEME_DESCRIPTIONS = {
  industrial: 'Grey canvas · navy sidebar',
  light_brand: 'White canvas · navy & lime',
  dark_brand: 'Dark navy for low light',
};

// sidebar · canvas · top bar · blocks — the three themes as the app paints them
const THUMBS = {
  industrial: { side: '#191970', canvas: '#eef0f6', bar: '#ffffff', block: '#ffffff', line: null, ink: '#d5d8e6' },
  light_brand: { side: '#191970', canvas: '#ffffff', bar: '#ffffff', block: '#f4f5fa', line: '#191970', ink: '#d5d8e6' },
  dark_brand: { side: '#0b0b3b', canvas: '#12124f', bar: '#12124f', block: '#25257a', line: null, ink: '#3a3a8f' },
};

function ThemeThumb({ value }) {
  const t = THUMBS[value] || THUMBS.industrial;
  return (
    <span aria-hidden="true" className="relative flex h-[92px] w-full overflow-hidden rounded-lg border border-black/5" style={{ background: t.canvas }}>
      <span className="flex w-[26px] shrink-0 flex-col items-center gap-[5px] pt-2" style={{ background: t.side }}>
        <span className="h-1 w-3 rounded-full bg-lime" />
        {[0, 1, 2, 3].map((index) => <span key={index} className="h-[3px] w-3 rounded-full bg-white/35" />)}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="h-3 w-full shrink-0" style={{ background: t.bar, borderBottom: t.line ? `3px solid ${t.line}` : '1px solid rgba(0,0,0,.05)' }} />
        <span className="flex flex-1 gap-2 p-2">
          <span className="flex flex-1 flex-col gap-1.5 rounded-md p-1.5 shadow-[0_1px_2px_rgba(0,0,0,.06)]" style={{ background: t.block }}>
            <span className="h-[3px] w-3/4 rounded-full" style={{ background: t.ink }} />
            <span className="h-[3px] w-1/2 rounded-full" style={{ background: t.ink }} />
          </span>
          <span className="flex flex-1 flex-col justify-between rounded-md p-1.5 shadow-[0_1px_2px_rgba(0,0,0,.06)]" style={{ background: t.block }}>
            <span className="h-[3px] w-2/3 rounded-full" style={{ background: t.ink }} />
            <span className="h-[7px] w-7 rounded-full bg-lime" />
          </span>
        </span>
      </span>
    </span>
  );
}

/**
 * value = the draft theme, saved = the theme everyone sees today.
 * Arrow keys move between the cards (one tab stop), like any radio group.
 */
export default function ThemePicker({ value, saved, onChange, disabled, labelledBy }) {
  const refs = useRef({});
  const options = THEME_OPTIONS;
  const previewing = value && saved && value !== saved;
  const current = options.find((option) => option.value === value);

  const onKeyDown = (event) => {
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    if (!(event.key in keys)) return;
    event.preventDefault();
    const index = options.findIndex((option) => option.value === value);
    const next = options[(index + keys[event.key] + options.length) % options.length];
    onChange(next.value);
    refs.current[next.value]?.focus();
  };

  return (
    <div className="min-w-0">
      {/* two columns on a phone, then cards of 146–188px: three in a row where they fit — never one card per row */}
      <div role="radiogroup" aria-labelledby={labelledBy} className="grid grid-cols-2 gap-3 sm:grid-cols-[repeat(auto-fill,minmax(146px,188px))]" data-testid="theme-picker">
        {options.map((option) => {
          const checked = option.value === value;
          const isCurrent = option.value === saved;
          return (
            <button
              key={option.value}
              ref={(node) => { refs.current[option.value] = node; }}
              type="button"
              role="radio"
              aria-checked={checked}
              tabIndex={checked ? 0 : -1}
              disabled={disabled}
              onClick={() => onChange(option.value)}
              onKeyDown={onKeyDown}
              className={cn(
                'group min-w-0 rounded-xl border bg-surface p-2 text-left transition-[border-color,box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60',
                checked ? 'border-2 border-brand p-[7px] shadow-raised' : 'border-line hover:border-[color:var(--ink-4)]'
              )}
              data-testid={`theme-option-${option.value}`}
            >
              <span className="relative block">
                <ThemeThumb value={option.value} />
                {isCurrent ? (
                  <span className="absolute right-1.5 top-1.5 rounded-full border border-line bg-surface px-1.5 text-[10.5px] font-bold leading-[18px] text-ink-2">Current</span>
                ) : checked ? (
                  <span className="absolute right-1.5 top-1.5 inline-flex items-center gap-1 rounded-full border border-brand-soft-border bg-brand-soft px-1.5 text-[10.5px] font-bold leading-[18px] text-brand-ink">
                    <Eye className="h-2.5 w-2.5" aria-hidden="true" /> Previewing
                  </span>
                ) : null}
              </span>
              <span className="mt-2 flex items-start gap-2 px-0.5">
                <span
                  aria-hidden="true"
                  className={cn(
                    'mt-px grid h-4 w-4 shrink-0 place-items-center rounded-full border-[1.5px]',
                    checked ? 'border-brand' : 'border-[color:var(--ink-4)]'
                  )}
                >
                  {checked ? <span className="h-2 w-2 rounded-full bg-brand" /> : null}
                </span>
                <span className="min-w-0 text-[13px] font-bold leading-[18px] text-ink">{option.label}</span>
              </span>
              <span className="mt-0.5 line-clamp-2 pl-6 text-[11.5px] leading-snug text-ink-3">{THEME_DESCRIPTIONS[option.value]}</span>
            </button>
          );
        })}
      </div>
      {/* the icon stays on the first text line (no wrapping onto a line of its own on phones) */}
      <p className="mt-2.5 flex items-start gap-1.5 text-[12px] leading-[18px] text-ink-3" aria-live="polite" data-testid="theme-preview-note">
        {previewing ? (
          <>
            <Eye className="mt-[2.5px] h-[13px] w-[13px] shrink-0" aria-hidden="true" />
            <span className="min-w-0">
              Previewing <b className="font-semibold text-ink-2">{current?.label}</b> — only you see it until you save.{' '}
              <button
                type="button"
                onClick={() => onChange(saved)}
                className="rounded font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="theme-stop-preview"
              >
                Stop preview
              </button>
            </span>
          </>
        ) : (
          <>
            <Palette className="mt-[2.5px] h-[13px] w-[13px] shrink-0" aria-hidden="true" />
            <span className="min-w-0">Pick a theme to preview it here first — nobody else sees it until you save.</span>
          </>
        )}
      </p>
    </div>
  );
}
